import * as React from 'react';
import { useMemo, useCallback } from 'react';
import { css } from '@emotion/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocalDiv from '../../../localization/LocalDiv';
import { DevPlanCollaborator } from './models';
import { COLORS } from '../../../globalStyle/colors';
import { faCheck, faBan, faTrash } from '@fortawesome/free-solid-svg-icons';
import Msg from '../../../localization/Msg';
import ActionIcon from '../../../layout/ActionIcon';
import { FLEX_COLUMN } from '../../../layout/flexBox';
import Spacer from '../../../layout/Spacer';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import ClamShell from '../../../layout/ClamShell';
import { useLoading } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';


const styles = {
  emailAndStatus: {
    root: [
      FLEX_COLUMN,
    ],
  },
  separator:      css`
    margin: 0 auto;
  `,
  deleteIcon:     {
    root: [
      css`
        cursor: pointer;
      `,
    ],
    icon: css`
      color: ${COLORS.DARK_GREY};
    `,
  },
  red:            css`
    color: ${COLORS.RED};
  `,
  green:          css`
    color: ${COLORS.GREEN};
  `,
};


type Props = {
  projectId: number;
  collaborator: DevPlanCollaborator;
  deleteCollaborator: () => void;
  className?: string;
}


/**
 * CollaboratorListItem: a component that represents a dev plan collaborator and provides visual feedback regarding
 * their current invitation acceptance status (i.e., whether they've accessed the dev plan yet)
 */
const CollaboratorListItem: React.FC<Props> = ({ projectId, collaborator, deleteCollaborator, className }) => {
  const handleClick = useCallback(
    event => {
      event.preventDefault();
      deleteCollaborator();
    },
    [deleteCollaborator],
  );

  const acceptedMsg    = useMsgText(Str.Accepted);
  const notAcceptedMsg = useMsgText(Str.NotAccepted);

  const { statusColor, statusIcon, statusMsg } = useMemo(
    () =>
      collaborator.accepted
        ? ({
          statusColor: styles.green,
          statusIcon:  faCheck,
          statusMsg:   acceptedMsg,
        }) : ({
          statusColor: styles.red,
          statusIcon:  faBan,
          statusMsg:   notAcceptedMsg,
        }),
    [acceptedMsg, collaborator.accepted, notAcceptedMsg],
  );

  const deletingCollaborator = useLoading(
    ActionType.DeleteDevPlanCollaboratorRequest,
    `${projectId}-${collaborator.email}`,
  );

  return (
    <ClamShell show={!deletingCollaborator}>
      <LocalDiv className={className}>
        <Spacer sizePx={12} />
        <FontAwesomeIcon icon={statusIcon} size={'2x'} css={statusColor} />
        <Spacer sizePx={12} />
        <LocalDiv css={styles.emailAndStatus.root}>
          <span>{collaborator.email}</span>
          <Spacer sizePx={4} />
          <Msg str={statusMsg} defaultMsg={statusMsg} css={statusColor} />
        </LocalDiv>
        <div css={styles.separator} />
        <ActionIcon
          onClick={handleClick}
          icon={faTrash}
          iconStyle={styles.deleteIcon.icon}
          css={styles.deleteIcon.root}
        />
        <Spacer sizePx={12} />
      </LocalDiv>
    </ClamShell>
  );
};

export default CollaboratorListItem;
